import { useEffect } from 'react';
import ApiLoadingOverlay from './components/ApiLoadingOverlay';
import AuthChecker from './components/AuthChecker';
import CustomerServiceButton from './components/CustomerServiceButton';
import FacebookSDKLoader from './components/FacebookSDKLoader';
import Header from './components/Header';
import LoginModal from './components/LoginModal';
import PaymentModal from './components/PaymentModal';
import PaymentSuccess from './components/PaymentSuccess';
import ProductList from './components/ProductList';
import RefreshDialog from './components/RefreshDialog';
import { BUILD_INFO } from './config/buildInfo';
import useStore from './store/useStore';

const detectWebPSupport = () => {
  if (typeof document === 'undefined') return false;

  try {
    const canvas = document.createElement('canvas');
    if (!canvas.getContext) return false;
    return canvas.toDataURL('image/webp').startsWith('data:image/webp');
  } catch {
    return false;
  }
};

const registerCrossDayDebugHelpers = () => {
  if (typeof window === 'undefined') return () => {};

  const canEnable =
    import.meta.env.DEV ||
    import.meta.env.VITE_ENABLE_CROSSDAY_DEBUG === 'true' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  if (!canEnable) return () => {};

  window.__setCrossDayRemainingSeconds = (seconds) => {
    const sec = Number(seconds);
    if (!Number.isFinite(sec)) {
      console.warn('[crossday-debug] invalid seconds:', seconds);
      return;
    }

    const safeSec = Math.max(0, Math.floor(sec));
    const nowSec = Math.floor(Date.now() / 1000);
    const store = useStore.getState();
    const currentUser = store.user;

    if (!currentUser) {
      console.warn('[crossday-debug] no authenticated user');
      return;
    }

    store.setUser({
      ...currentUser,
      todayEnd: nowSec + safeSec,
      today_end: nowSec + safeSec,
      serverNow: nowSec,
      serverTimeOffsetMs: 0,
    });

    console.log('[crossday-debug] updated remaining seconds:', safeSec, {
      todayEnd: nowSec + safeSec,
      serverNow: nowSec,
    });
  };

  window.__getCrossDayDebugState = () => {
    const currentUser = useStore.getState().user;
    const nowSec = Math.floor(Date.now() / 1000);
    const todayEnd = Number(currentUser?.todayEnd ?? currentUser?.today_end);
    const remain = Number.isFinite(todayEnd) ? Math.max(todayEnd - nowSec, 0) : null;

    const snapshot = {
      todayEnd: currentUser?.todayEnd,
      today_end: currentUser?.today_end,
      serverNow: currentUser?.serverNow,
      serverTimeOffsetMs: currentUser?.serverTimeOffsetMs,
      remainingSecondsByLocalNow: remain,
    };

    console.log('[crossday-debug] state', snapshot);
    return snapshot;
  };

  return () => {
    delete window.__setCrossDayRemainingSeconds;
    delete window.__getCrossDayDebugState;
  };
};

const hasGameAuthParamsInUrl = () => {
  const query = window.location.search || '';
  const hash = window.location.hash || '';
  const target = `${query}${hash}`;
  return /(?:\?|&)(ws_auth|fpid|playerid|token|gametoken)=/.test(target);
};

function App() {
  const {
    isAuthenticated,
    authCheckDone,
    loginModalOpen,
    setLoginModalOpen,
    paymentModalOpen,
    setPaymentModalOpen,
    selectedProduct,
    paymentSuccessOpen,
    setPaymentSuccessOpen,
    lastOrder,
    logout
  } = useStore();

  // 页面加载时检查sessionStorage标记（处理浏览器刷新按钮的情况）
  useEffect(() => {
    // 游戏透传登录优先，避免旧的 sessionExpired 标记冲掉本次自动登录
    if (hasGameAuthParamsInUrl()) {
      sessionStorage.removeItem('sessionExpired');
      return;
    }

    const sessionExpired = sessionStorage.getItem('sessionExpired');
    if (sessionExpired === 'true') {
      // 清除标记
      sessionStorage.removeItem('sessionExpired');
      // 确保用户状态已清除（防止localStorage中还有残留）
      logout();
    }
  }, []);

  useEffect(() => {
    const cleanup = registerCrossDayDebugHelpers();
    return cleanup;
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.__buildInfoLogged) return;

    const buildCommitShort = String(BUILD_INFO?.VER_COMMIT || '').slice(0, 8);
    const hasBackendConfig = Boolean(import.meta.env.VITE_API_BASE_URL);
    const supportsWebP = detectWebPSupport();
    const headerStyle = 'color:#ff6b6b;font-weight:700;';
    const lineStyle = 'color:#ff6b6b;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-weight:700;';

    console.group('%c###Shell.start -> Object', headerStyle);
    console.log('%cSHELL:', lineStyle);
    console.log(`%c[MODE]: ${import.meta.env.MODE}`, lineStyle);
    console.log(`%c[VERSION]: ${BUILD_INFO?.VERSION} [VER_CODE]: ${buildCommitShort}`, lineStyle);
    console.log(`%c[CDN] back:${hasBackendConfig} [Webp]: ${supportsWebP}`, lineStyle);
    console.log(`%c[BUILDER]: ${BUILD_INFO?.VER_USER}`, lineStyle);
    console.log(`%c[BUILD_TIME]: ${BUILD_INFO?.BUILD_TIME}`, lineStyle);
    console.groupEnd();

    if (typeof window !== 'undefined') {
      window.__buildInfoLogged = true;
    }
  }, []);

  // 未登录时立即弹出登录框
  useEffect(() => {
    if (authCheckDone && !isAuthenticated && !loginModalOpen) {
      setLoginModalOpen(true);
    }
  }, [authCheckDone, isAuthenticated, loginModalOpen, setLoginModalOpen]);

  return (
    <FacebookSDKLoader>
      <AuthChecker />
      <div className="relative min-h-screen bg-[linear-gradient(180deg,#eef7ff_0%,#f8efff_44%,#fff8eb_100%)] text-[#1e1535] dark:bg-velvet dark:text-white">
        {/* Radial glow overlays */}
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_15%_10%,rgba(123,165,255,0.18),transparent_18%),radial-gradient(circle_at_80%_0%,rgba(255,215,146,0.24),transparent_16%),radial-gradient(circle_at_85%_30%,rgba(207,180,255,0.2),transparent_24%),radial-gradient(circle_at_10%_65%,rgba(124,206,255,0.16),transparent_14%)] dark:bg-[radial-gradient(circle_at_15%_10%,rgba(247,196,90,0.12),transparent_18%),radial-gradient(circle_at_80%_0%,rgba(80,196,255,0.12),transparent_16%),radial-gradient(circle_at_85%_30%,rgba(115,45,172,0.24),transparent_26%),radial-gradient(circle_at_10%_65%,rgba(255,128,89,0.11),transparent_14%)]" />

        <div className={isAuthenticated ? "" : "pointer-events-none select-none"}>
          <div className={isAuthenticated ? "" : "blur-[2px] opacity-80"}>
            <Header />

            <main className="mx-auto max-w-7xl px-3 pb-12 pt-5 sm:px-6 sm:pb-16 sm:pt-8 lg:px-8">
              <ProductList />
            </main>

            {/* Reminder Section */}
            <section className="mx-auto mt-2 max-w-7xl px-3 pb-8 sm:px-6 lg:px-8">
              <div className="rounded-[1.6rem] border border-[#dfe6ff] bg-white/60 px-5 py-5 text-sm leading-7 text-[#66728e] shadow-[0_14px_28px_rgba(96,118,195,0.08)] dark:border-white/10 dark:bg-white/5 dark:text-white/62">
                <p className="font-display text-base font-black uppercase tracking-[0.08em] text-[#3f4d7f] dark:text-white/78">
                  Reminder
                </p>
                <p>* Virtual Coin/Gem deals in this store are dynamic and may vary over time for different users.</p>
                <p>* All web store purchases must be claimed inside the game.</p>
              </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-[#dfe6ff] bg-white/45 dark:border-white/10 dark:bg-black/20">
              <div className="mx-auto flex max-w-7xl flex-col gap-3 px-3 py-6 text-center text-sm text-[#66728e] dark:text-white/55 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8 lg:text-left">
                <div className="flex flex-wrap items-center justify-center gap-4 lg:justify-start">
                  <a target="_blank" href="https://www.centurygames.com/terms-of-service/" className="transition hover:text-[#4857d7] dark:hover:text-goldSoft">
                    Terms of Service
                  </a>
                  <a target="_blank" href="https://www.centurygames.com/privacy-policy/" className="transition hover:text-[#4857d7] dark:hover:text-goldSoft">
                    Privacy Policy
                  </a>
                  <a target="_blank" href="https://www.facebook.com/FTS.Fantasy.Slots" className="transition hover:text-[#4857d7] dark:hover:text-goldSoft">
                    Fan Page
                  </a>
                </div>
                <p>Copyright © 2026 Fantasy Slots. All rights reserved.</p>
              </div>
            </footer>
          </div>
        </div>
      </div>

      <LoginModal />
      <PaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        product={selectedProduct}
      />
      <PaymentSuccess
        open={paymentSuccessOpen}
        onClose={() => setPaymentSuccessOpen(false)}
        order={lastOrder}
      />
      <CustomerServiceButton />
      <ApiLoadingOverlay />
      <RefreshDialog />
    </FacebookSDKLoader>
  );
}

export default App;
